import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useState } from "react";
import { Home, ShieldAlert, Sparkles, Activity as ActivityIcon, User, MailWarning, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { IncidentAlerts } from "@/components/IncidentAlerts";
import { toast } from "sonner";

const tabs = [
  { to: "/home", label: "Home", icon: Home, id: "home" },
  { to: "/safety", label: "Safety", icon: ShieldAlert, id: "safety" },
  { to: "/assistant", label: "Guardian", icon: Sparkles, id: "assistant" },
  { to: "/activity", label: "Activity", icon: ActivityIcon, id: "activity" },
  { to: "/profile", label: "Profile", icon: User, id: "profile" },
];

const VerifyEmailBanner = () => {
  const { user, resendVerification } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(false);
  const show = user && user.email_verified === false && user.auth_method !== "phone" && !dismissed;
  if (!show) return null;
  const resend = async () => {
    setBusy(true);
    try { await resendVerification(); toast.success("Verification email sent — check your inbox."); }
    catch { toast.error("Could not send email. Please try again."); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="verify-email-banner" className="bg-[#F59E0B]/15 border-b border-[#F59E0B]/30 px-4 py-2.5 flex items-center gap-2.5">
      <MailWarning className="w-4 h-4 text-[#92400e] shrink-0" />
      <p className="text-xs text-[#92400e] font-semibold flex-1">Verify your email to secure your account.
        <button onClick={resend} disabled={busy} data-testid="verify-email-resend" className="underline ml-1 disabled:opacity-50">{busy ? "Sending…" : "Resend link"}</button>
      </p>
      <button onClick={() => setDismissed(true)} data-testid="verify-email-dismiss" className="text-[#92400e]/60"><X className="w-4 h-4" /></button>
    </div>
  );
};

export const AppLayout = () => {
  const location = useLocation();
  return (
    <div className="min-h-screen bg-[#FFF7E6]">
      <div className="max-w-lg mx-auto pb-28 min-h-screen">
        <VerifyEmailBanner />
        <IncidentAlerts />
        <Outlet key={location.pathname} />
      </div>
      <nav
        data-testid="bottom-nav"
        className="fixed bottom-0 left-0 w-full bg-white/95 backdrop-blur-md border-t border-[#111827]/10 pb-safe pt-2 px-2 z-50 rounded-t-3xl shadow-[0_-4px_24px_rgba(0,0,0,0.06)]"
      >
        <div className="max-w-lg mx-auto flex justify-between items-center">
          {tabs.map((t) => (
            <NavLink
              key={t.id}
              to={t.to}
              data-testid={`nav-${t.id}`}
              className="flex flex-col items-center justify-center min-h-[56px] min-w-[56px] flex-1"
            >
              {({ isActive }) => (
                <div className={`flex flex-col items-center gap-1 px-2 py-1 rounded-xl transition-colors ${isActive ? "text-[#111827] bg-[#C4B5FD]/40" : "text-[#111827]/45"}`}>
                  <t.icon className="w-6 h-6" strokeWidth={isActive ? 2.4 : 2} />
                  <span className="text-[11px] font-semibold leading-none">{t.label}</span>
                </div>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
};
